import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Lightbulb, TrendingUp, Users } from "lucide-react";

const values = [
  {
    icon: Target,
    title: "Präzision",
    desc: "Massgeschneiderte Lösungen, die genau auf Ihre Anforderungen abgestimmt sind.",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    desc: "Einsatz modernster Technologien für zukunftssichere digitale Lösungen.",
  },
  {
    icon: TrendingUp,
    title: "Effizienz",
    desc: "Optimierte Prozesse, die Ihrem Unternehmen helfen, schneller zu wachsen.",
  },
  {
    icon: Users,
    title: "Partnerschaft",
    desc: "Enge Zusammenarbeit mit unseren Kunden für nachhaltige Ergebnisse.",
  },
];

export default function About() {
  return (
    <div>
      {/* Header */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="about-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-sm font-semibold tracking-wide uppercase text-[#FF3B30]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Über uns
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F172A] mt-3 max-w-3xl" style={{ fontFamily: 'Plus Jakarta Sans' }} data-testid="about-page-title">
            Über ServicePoint Huwiler
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed mt-6 max-w-2xl">
            Technologische Expertise trifft auf praktische Geschäftserfahrung.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A] mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Wer wir sind
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  ServicePoint Huwiler verbindet technologisches Know-how mit praktischer Geschäftserfahrung. Das Unternehmen konzentriert sich auf die Entwicklung moderner digitaler Lösungen, die Organisationen dabei helfen, effizienter zu arbeiten und sich an die digitale Welt anzupassen.
                </p>
                <p>
                  Unsere Mission ist es, zuverlässige digitale Dienstleistungen zu liefern, die Innovation, Wachstum und betriebliche Effizienz unterstützen.
                </p>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-sm overflow-hidden bg-slate-100">
              <img
                src="https://images.unsplash.com/photo-1758691737182-d42aefd6dee8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBtZWV0aW5nJTIwYnJpZ2h0JTIwbWluaW1hbGlzdCUyMHBlb3BsZXxlbnwwfHx8fDE3NzI3MTE2Mzd8MA&ixlib=rb-4.1.0&q=85"
                alt="Modernes Büro"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <span className="text-sm font-semibold tracking-wide uppercase text-[#FF3B30]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Unsere Werte
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A] mt-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Was uns antreibt
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <div
                key={v.title}
                className="bg-white border border-slate-100 rounded-sm p-8 hover:shadow-lg transition-all duration-300"
                data-testid={`value-card-${i}`}
              >
                <div className="w-10 h-10 rounded-sm bg-[#FF3B30]/5 flex items-center justify-center mb-5">
                  <v.icon size={20} className="text-[#FF3B30]" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  {v.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {v.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0F172A] mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Lassen Sie uns zusammenarbeiten
          </h2>
          <p className="text-slate-600 max-w-lg mx-auto mb-8">
            Interessiert an einer Zusammenarbeit? Kontaktieren Sie uns und erfahren Sie, wie wir Ihnen helfen können.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 bg-[#FF3B30] text-white h-12 px-8 rounded-sm font-medium transition-all duration-300 hover:bg-[#E5352B] shadow-sm hover:shadow-lg"
            data-testid="about-cta-button"
          >
            Kontakt aufnehmen
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
